import {
  Calendar,
  FileText,
  Lock,
  MessageSquare,
  PhoneForwarded,
  Scale,
} from 'lucide-react';

import { IndustryPage, type IndustryPageConfig } from '@/components/marketing/industry-page';

const config: IndustryPageConfig = {
  seoTitle: 'AI Receptionist for Law Firms — Client Intake, 24/7 Call Answering',
  seoDescription:
    'Never miss a prospective client call. Our AI receptionist for law firms captures intake details, answers FAQs, and transfers urgent calls — accurately, professionally, around the clock.',
  path: '/industries/legal',
  badge: 'Law Firms',
  headline: 'First impressions that win clients — even at 11pm on a Friday.',
  subheadline:
    'An AI legal receptionist that captures client intake details, answers practice area questions, and transfers to your team when the matter is urgent — 24/7, with the tone your firm demands.',
  pains: [
    'Prospective clients in distress call after hours, reach voicemail, and call a competitor instead.',
    'Intake calls take 10–15 minutes of paralegal time before a single qualifying question is answered.',
    'Inconsistent intake data makes conflict checks and matter opening slower than they need to be.',
  ],
  features: [
    {
      icon: Scale,
      title: 'Practice area Q&A',
      desc: 'Answers questions about your practice areas, fee structures, and consultation process — from your approved content, not invented answers.',
    },
    {
      icon: MessageSquare,
      title: 'Client intake capture',
      desc: 'Collects prospect name, contact, matter type, and urgency. Structured intake data delivered to your dashboard, ready for conflict check.',
    },
    {
      icon: PhoneForwarded,
      title: 'Urgent matter transfer',
      desc: 'Custody emergencies, bail calls, injunction deadlines — define your urgency rules and the agent transfers immediately with caller context.',
    },
    {
      icon: Calendar,
      title: 'Consultation scheduling',
      desc: 'Captures preferred consultation times and attorney preference. Your team confirms and books — no back-and-forth for the client.',
    },
    {
      icon: Lock,
      title: 'Professional, consistent tone',
      desc: 'Configure the agent\'s voice, greeting, and persona to match your firm\'s brand. Every caller receives the same high standard.',
    },
    {
      icon: FileText,
      title: 'Full call transcripts',
      desc: 'Every call logged with transcript and AI summary. Intake data structured for easy review before the first consultation.',
    },
  ],
  faqs: [
    {
      q: 'Can the AI conduct a conflict check?',
      a: 'The agent captures the intake details needed for a conflict check — opposing party name, matter type, and referring attorney. Your team runs the actual check.',
    },
    {
      q: 'Will it give legal advice?',
      a: 'No. The agent answers from your approved content only (practice areas, fees, process). It is configured to collect information, not to advise.',
    },
    {
      q: 'Can I customise the intake questions per practice area?',
      a: 'Yes — configure different intake flows for family law, criminal, commercial, and other practice areas in your knowledge base.',
    },
    {
      q: 'What happens for genuinely urgent matters like bail applications?',
      a: 'Define your urgency transfer rules. The agent detects urgency in the caller\'s language and warm-transfers to your on-call mobile with a spoken briefing.',
    },
    {
      q: 'Does it work after hours and on weekends?',
      a: 'Yes — 24/7, including public holidays. That\'s when distressed clients call and need a professional response.',
    },
  ],
  ctaHeadline: 'Stop losing clients to out-of-hours voicemail.',
  ctaSub: 'Professional intake. Every call. Set up in under 10 minutes.',
};

export function LegalIndustryPage(): JSX.Element {
  return <IndustryPage config={config} />;
}
